import { useEffect, useState } from "react";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import { useTranslation } from "react-i18next";
import { useCreateCommentMutation, useDeleteCommentMutation, useUpdateCommentMutation } from "@/features/bughurd/api";
import { isCloseToBottom } from "./BoardTableView";
import { toast } from "react-toastify";
import { Avatar, Box, Button, CircularProgress, IconButton, Popover, TextField, Typography } from "@mui/material";
import { Edit, Delete as DeleteIcon, ChatBubbleOutline } from "@mui/icons-material";

// CommentsPopover component
const CommentsPopover = ({
  anchorEl,
  onClose,
  comments,
  isLoading,
  taskName,
  taskId,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [commentInput, setCommentInput] = useState("");
  const [createComment, { isLoading: isSubmitting }] =
    useCreateCommentMutation();
  const [updateComment, { isLoading: isUpdating }] = useUpdateCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  // State for editing comments
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  // Determine if popover should show above
  const [showAbove, setShowAbove] = useState(false);

  useEffect(() => {
    if (anchorEl) {
      const isNearBottom = isCloseToBottom(anchorEl);
      setShowAbove(isNearBottom);
    }
  }, [anchorEl]);

  const handleInputChange = (e) => {
    setCommentInput(e.target.value);
  };

  const handleAddComment = async () => {
    if (!commentInput.trim() || !taskId) return;

    try {
      const payload = {
        itemId: taskId,
        comment: commentInput.trim(),
        module: "BUG_TRACKING",
        isComment: true,
        type: "PUBLIC",
      };

      await createComment(payload).unwrap();
      setCommentInput("");
      toast.success(t("Comment added successfully"));
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error(
        t("Failed to add comment") + ": " + (error.data?.message || t("Unknown error"))
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleAddComment();
    }
  };
//   const renderPageNumbers = () => {
//     const totalPages = Math.ceil(totalRows / paginationModel.pageSize);
//     const currentPage = paginationModel.page;
//     const pageNumbers = [];

//     // Show maximum 5 page numbers
//     let startPage = Math.max(0, currentPage - 2);
//     let endPage = Math.min(totalPages - 1, currentPage + 2);

//     // Adjust if we're near the beginning or end
//     if (endPage - startPage < 4) {
//       if (startPage === 0) {
//         endPage = Math.min(totalPages - 1, startPage + 4);
//       } else if (endPage === totalPages - 1) {
//         startPage = Math.max(0, endPage - 4);
//       }
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       pageNumbers.push(
//         <Button
//           key={i}
//           size="small"
//           variant={i === currentPage ? "contained" : "outlined"}
//           onClick={() => setPaginationModel((prev) => ({ ...prev, page: i }))}
//           sx={{
//             minWidth: "32px",
//             width: "32px",
//             height: "32px",
//             p: 0,
//             borderColor: "divider",
//             color: i === currentPage ? "white" : "text.primary",
//             "&:hover": {
//               borderColor: "primary.main",
//               backgroundColor:
//                 i === currentPage ? "primary.dark" : "action.hover",
//             },
//           }}
//         >
//           {i + 1}
//         </Button>
//       );
//     }

//     return pageNumbers;
//   };
  const handleEditComment = (commentId, commentText) => {
    setEditingCommentId(commentId);
    setEditCommentText(commentText);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleSaveEdit = async () => {
    if (!editCommentText.trim() || !editingCommentId) return;

    try {
      await updateComment({
        commentId: editingCommentId,
        comment: editCommentText.trim(),
      }).unwrap();

      setEditingCommentId(null);
      setEditCommentText("");
      toast.success(t("Comment updated successfully"));
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(
        t("Failed to update comment") + ": " + (error.data?.message || t("Unknown error"))
      );
    }
  };

  const openDeleteModal = (commentId) => {
    setCommentToDelete(commentId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteComment(commentToDelete).unwrap();
      toast.success(t("Comment deleted successfully"));
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error(
        t("Failed to delete comment") + ": " + (error.data?.message || t("Unknown error"))
      );
      closeDeleteModal();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `${t("Today")} ${t("at")} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays === 1) {
      return `${t("Yesterday")} ${t("at")} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    }
  };

  const isAnyLoading = isLoading || isSubmitting || isUpdating || isDeleting;

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={
          showAbove
            ? {
                vertical: "top",
                horizontal: "left",
              }
            : {
                vertical: "bottom",
                horizontal: "left",
              }
        }
        transformOrigin={
          showAbove
            ? {
                vertical: "bottom",
                horizontal: "left",
              }
            : {
                vertical: "top",
                horizontal: "left",
              }
        }
        PaperProps={{
          elevation: 3,
          sx: {
            width: 400,
            borderRadius: 2,
            maxHeight: "calc(100vh - 100px)",
            ...(showAbove ? { marginBottom: "8px" } : {}),
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {taskName ? t('Comments on "{{taskName}}"', { taskName }) : t("Comments")}
          </Typography>

          {isAnyLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
                flexGrow: 1,
              }}
            >
              <CircularProgress size={30} thickness={4} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "auto",
                  mt: 1,
                  mb: 2,
                  maxHeight: "400px",
                  minHeight: "100px",
                }}
              >
                {comments && comments.length > 0 ? (
                  comments.map((comment) => (
                    <Box
                      key={comment._id}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: "primary.main",
                          }}
                        >
                          {comment.createdBy?.fullName?.charAt(0) || "U"}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ mr: 1 }}
                              >
                                {comment.createdBy?.fullName || t("Unknown User")}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(comment.createdAt)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleEditComment(
                                    comment._id,
                                    comment.comment
                                  )
                                }
                                sx={{
                                  padding: "2px",
                                  color: "primary.main",
                                  "&:hover": { color: "primary.dark" },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => openDeleteModal(comment._id)}
                                sx={{
                                  padding: "2px",
                                  color: "error.main",
                                  "&:hover": { color: "error.dark" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {editingCommentId === comment._id ? (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                fullWidth
                                multiline
                                size="small"
                                value={editCommentText}
                                onChange={(e) =>
                                  setEditCommentText(e.target.value)
                                }
                                sx={{ mb: 1 }}
                                autoFocus
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={isUpdating}
                                >
                                  {t("Cancel")}
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleSaveEdit}
                                  disabled={
                                    !editCommentText.trim() || isUpdating
                                  }
                                >
                                  {t("Save")}
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                mt: 0.5,
                              }}
                            >
                              {comment.comment}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      color: "text.secondary",
                    }}
                  >
                    <ChatBubbleOutline
                      sx={{ fontSize: 40, mb: 1, opacity: 0.5 }}
                    />
                    <Typography>{t("No comments yet")}</Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: "auto",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  pt: 2,
                }}
              >
                <Box
                  component="form"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAddComment();
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder={t("Add a comment...")}
                    value={commentInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                    disabled={isSubmitting}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t("Press Ctrl+Enter to submit")}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!commentInput.trim() || isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        t("Comment")
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Popover>

      <DeleteConfirmModal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteComment}
        loading={isDeleting}
        title={t("Delete Comment")}
        confirmMessage={t("Are you sure you want to delete this comment?")}
        okText={t("Delete")}
      />
    </>
  );
};

export default CommentsPopover;